import type { Timestamp } from 'firebase/firestore';

/**
 * Partner / household cheerleading — the one-sided share that lets a partner
 * see the member's streak and send short notes of encouragement, without
 * ever seeing a raw tracker value.
 *
 * Data split:
 * - `users/{memberUid}/partner_links/{linkId}` — private, owner-only record
 *   of the invite. The member can list, label, and revoke these.
 * - `partner_views/{linkId}` — public(ish): any request carrying the linkId
 *   can read it. Holds ONLY shareable fields (streak, longest, badge count,
 *   member first name). No raw tracker data EVER lives here.
 * - `users/{memberUid}/cheerlead_notes/{noteId}` — member-readable feed.
 *   Partners create these without auth; Firestore rules validate that the
 *   linkId on the note matches an active `partner_views` doc for this
 *   member.
 */

export type PartnerLinkStatus = 'active' | 'revoked';

export interface PartnerLinkRecord {
  id: string;
  label: string;
  status: PartnerLinkStatus;
  createdAt?: Timestamp | null;
  revokedAt?: Timestamp | null;
  lastCheerleadAt?: Timestamp | null;
  cheerleadCount: number;
}

/**
 * Public-readable projection of what a partner sees. Never includes raw
 * tracker fields (water, protein, caffeine, mood, etc). If we ever add a new
 * tracker data field here, that's a privacy bug — review the spec first.
 */
export interface PartnerViewRecord {
  memberUid: string;
  memberFirstName: string;
  status: PartnerLinkStatus;
  currentStreak: number;
  longestStreak: number;
  badgesEarnedCount: number;
  updatedAt?: Timestamp | null;
}

export interface CheerleadNoteRecord {
  id: string;
  linkId: string;
  partnerName: string;
  message: string;
  sentAt?: Timestamp | null;
  read: boolean;
}

export const CHEERLEAD_MESSAGE_MAX = 200;
export const PARTNER_NAME_MAX = 40;
export const PARTNER_LABEL_MAX = 40;
